<template>
  <ParetoChart
    ref="chart"
    :unit="unit"
    :scale="scale"
    :title="title"
    :loading="loading"
    @selectpart="fetchData"
    :criticalpart="criticalpart"
    :columncolor="columncolor"
  />
</template>

<script>
import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations,
} from 'vuex';
// import { formatDate } from '@shopworx/services/util/date.service';
import ParetoChart from '../ParetoChart.vue';

export default {
  name: 'CriticalPart',
  props: {
    title: { type: String, default: '' },
    scale: { type: Number, default: 1 },
    unit: { type: String, default: '' },
    partindex: { type: Number, default: 0 },
    widgetnumber: { type: Number, default: 1 },
    columncolor: { type: String, default: '#495364' },
  },
  components: { ParetoChart },
  data() {
    return {
      loading: false,
      criticalpart: null,
      fetchDataSerial: 0,
      fetchCriticalPartSerial: 0,
    };
  },
  computed: {
    ...mapState('cellwiseQualityDashboard', [
      'shifts',
      'yestedayworstpart',
      'currentDate',
      'dataFromJSON',
      'selectedCell',
    ]),
    ...mapGetters('cellwiseQualityDashboard', ['activeSite']),
    cellname() {
      return this.selectedCell || this.$route.query.selectedcell || '';
    },
  },
  watch: {
    cellname: {
      async handler(newVal, oldVal) {
        if (newVal !== oldVal && !this.dataFromJSON) {
          // Reset state to avoid stale data
          this.criticalpart = null;
          if (this.$refs.chart) {
            this.$refs.chart.selectedPart = '';
          }
          await this.fetchCriticalPart();
          await this.fetchData();
        }
      },
    },
    yestedayworstpart: {
      handler(newVal, oldVal) {
        if (!this.dataFromJSON && newVal !== oldVal) {
          this.fetchData(false, false);
        }
      },
    },
    currentDate(newVal, oldVal) {
      if (newVal && newVal !== oldVal && !this.dataFromJSON) {
        this.fetchData(false, false);
      }
    },
  },
  async mounted() {
    await this.fetchCriticalPart();
    await this.fetchData();
  },
  methods: {
    ...mapActions('cellwiseQualityDashboard', [
      'getReports',
      'createRecord',
      'updateRecord',
      'getCriticalPart',
    ]),
    ...mapMutations('cellwiseQualityDashboard', ['setPlanParts']),

    normalizePartName(value) {
      return String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    },

    resolvePartName(target, candidates = []) {
      const normalizedTarget = this.normalizePartName(target);
      if (!normalizedTarget) return '';

      const matched = candidates.find((candidate) => (
        this.normalizePartName(candidate) === normalizedTarget
      ));

      return matched || '';
    },

    async fetchData(part = false, loading = true) {
      this.fetchDataSerial += 1;
      const requestSerial = this.fetchDataSerial;
      if (loading) this.loading = true;
      if (part) {
        await this.setCriticalPart(part);
        await this.fetchCriticalPart();
      }

      if (!this.currentDate) {
        if (requestSerial === this.fetchDataSerial) {
          this.loading = false;
        }
        return;
      }

      const dateStr = String(this.currentDate);
      const targetDate = new Date(
        parseInt(dateStr.slice(0, 4), 10),
        parseInt(dateStr.slice(4, 6), 10) - 1,
        parseInt(dateStr.slice(6, 8), 10),
      );
      targetDate.setDate(targetDate.getDate() - 1);
      const start = parseInt(`${targetDate.getFullYear()}${String(targetDate.getMonth() + 1)
        .padStart(2, '0')}${String(targetDate.getDate()).padStart(2, '0')}`, 10);

      const payload = {
        start,
        end: start,
        siteid: this.activeSite || 0,
        cellname: this.cellname,
        partval: '',
      };

      const res = await this.getReports({ reportName: 'hourlyrejectionbyreason', payload });
      if (requestSerial !== this.fetchDataSerial) return;
      const data = (typeof res === 'string' ? JSON.parse(res) : res)?.reportData || res;

      // PASS THE 'part' ARGUMENT HERE
      this.setOptions(data, part);
      if (requestSerial === this.fetchDataSerial) {
        this.loading = false;
      }
    },

    async setCriticalPart(part) {
      const payload = {
        partname: part,
        cell: this.cellname,
        widget: this.widgetnumber,
        siteName: this.siteName,
      };
      if (this.criticalpart) {
        // eslint-disable-next-line dot-notation
        await this.updateRecord({ id: this.criticalpart['_id'], elementData: payload });
        await this.fetchCriticalPart();
      } else {
        await this.createRecord(payload);
        await this.fetchCriticalPart();
      }
    },

    async fetchCriticalPart() {
      this.fetchCriticalPartSerial += 1;
      const requestSerial = this.fetchCriticalPartSerial;
      const query = `?query=widget==${this.widgetnumber}%26%26cell=="${this.cellname}"`
        + '&sortquery=modifiedtimestamp==-1&pagenumber=1&pagesize=1';
      try {
        const res = await this.getCriticalPart(query);
        if (requestSerial !== this.fetchCriticalPartSerial) return;
        this.criticalpart = (Array.isArray(res) && res.length) ? res[0] : null;
      } catch (e) {
        if (requestSerial === this.fetchCriticalPartSerial) {
          this.criticalpart = null;
        }
      }
    },

    setOptions(data, explicitPart = null) {
      const safeData = Array.isArray(data) ? data : [];
      const reasonMap = {};
      const uniqueParts = [...new Set(safeData.map((i) => i.partname).filter(Boolean))];
      if (uniqueParts.length > 0) this.setPlanParts(uniqueParts);

      let selectedPart = explicitPart || this.criticalpart?.partname || '';

      const resolvedSelectedPart = this.resolvePartName(selectedPart, uniqueParts);
      if (resolvedSelectedPart) {
        selectedPart = resolvedSelectedPart;
      } else {
        const fallbackSource = this.yestedayworstpart?.[this.partindex]?.name;
        const fallbackPart = this.resolvePartName(fallbackSource, uniqueParts)
          || uniqueParts[this.partindex]
          || '';
        selectedPart = fallbackPart;
      }

      if (this.$refs.chart) {
        this.$refs.chart.selectedPart = selectedPart;

        safeData.forEach((row) => {
          if (this.normalizePartName(row.partname) === this.normalizePartName(selectedPart)) {
            const reason = row.reason || 'Unknown';
            reasonMap[reason] = (reasonMap[reason] || 0) + (Number(row.rejected) || 0);
          }
        });

        const sorted = Object.keys(reasonMap).sort((a, b) => reasonMap[b] - reasonMap[a]);
        this.$refs.chart.assignData({
          categories: sorted,
          series: sorted.map((r) => reasonMap[r]),
        });
      }
    },
  },
};
</script>
